import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { IQuestion } from 'cat-shared/utils'
import { usePageStateContext } from 'client/hooks'

import {
  AppButton,
  AppHeader,
  AppMessageBox,
  AppSegment,
} from 'client/ui-building-blocks'

import AnswersListItem from '../QuestionAnswersListItem/question-answers-list-item'

type Props = {
  question: IQuestion
}

export const QuestionVoteResults = ({ question }: Props) => {
  const { t } = useTranslation()

  const { showQuestionVoteStats } = usePageStateContext()

  const onReadMoreBtnClick = useCallback(() => {
    window.open(`https://google.com/search?q=${question.text}`, '_blank')
  }, [question.text])

  const totalVotesNum = question.options.reduce(
    (acc, current, i) => (acc += question.answeredStats?.all[i]),
    0
  )

  const statsData = question.options.map((option, i) => {
    const numOfVotes = question.answeredStats?.all[i]
    const percentage = Number(((numOfVotes / totalVotesNum) * 100).toFixed(1))

    return {
      no: Number(i + 1),
      votes: numOfVotes,
      percentage,
      didAuthUserVote: question.answeredStats?.byRequestor.includes(i),
    }
  })

  const fullyCorrect =
    question.correctOptions.sort((a, b) => a - b).toString() ===
    question.answeredStats.byRequestor.sort((a, b) => a - b).toString()

  return (
    <>
      {showQuestionVoteStats ? (
        <>
          <AppSegment
            headerProps={{
              text: 'History of selected answers',
              size: 'small',
              marginBottom: 'small',
            }}
            disableHeaderOnClickScroll
            marginBottom="small"
          >
            {statsData
              .sort((a, b) => b.votes - a.votes)
              .map((item, i) => {
                const votesCount = item.votes > 0 ? `${item.votes}x` : '-'

                return (
                  <AnswersListItem
                    key={i}
                    last={i === statsData.length - 1}
                    marked={i < statsData.length && item.didAuthUserVote}
                    content={question.options[item.no - 1]}
                    count={{
                      item: votesCount,
                      percentage: statsData[i].percentage,
                      total: (totalVotesNum || 0) + 'x',
                    }}
                  />
                )
              })}
          </AppSegment>
        </>
      ) : null}
      <AppMessageBox
        headerNode={
          <AppHeader
            text={
              fullyCorrect ? t('question.content.correct') : t('question.content.wrong')
            }
            iconProps={{
              name: fullyCorrect ? 'SMILE' : 'FROWN',
              size: 'xlarge',
              variant: fullyCorrect ? 'success' : 'error',
            }}
          />
        }
        paragraphs={[
          `${t(fullyCorrect ? 'question.content.yourAnswers' : 'question.content.correctAnswers', { count: question.correctOptions.length })}:`,
          ...question.correctOptions.map(i => `- ${question.options[i]}`),
          '',
          question.explanation,
        ]}
        buttonNode={
          <AppButton
            variant="outlined"
            text="Read more"
            iconName="SHARE_SQUARE"
            iconPosition="right"
            position="center"
            onClick={onReadMoreBtnClick}
          />
        }
      />
    </>
  )
}
